import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ProjectModelWithRelations, ComponentDefinitionControllerService } from '@rappider/rappider-sdk';
import { getProjectModelsWithFields } from 'libs/shared/src/lib/state-selectors';
import * as ProjectModelActions from 'libs/project/src/lib/states/project-model-state/project-model.actions';
import { EntityVisualizationState } from '../utils/entity-interfaces';
import { ComponentGeneratorService } from 'libs/content-editor/src/lib/services/component-generator.service';

/**
 * EntityVisualizationService manages entity visualization state and model interactions.
 * Handles model selection, splitter state, and visualization lifecycle management.
 * Provides centralized state management for entity visualization components.
 */
@Injectable({
  providedIn: 'root'
})
export class EntityVisualizationService implements OnDestroy {
  // Visualization State Management
  private visualizationState$ = new BehaviorSubject<EntityVisualizationState>({
    isLoading: false,
    loadingMessage: '',
    selectedModel: null,
    availableModels: [],
    lastCreatedModel: null,
    shouldOpenSplitter: false
  });

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<unknown>,
    private actions$: Actions,
    private componentGeneratorService: ComponentGeneratorService,
    private componentDefinitionControllerService: ComponentDefinitionControllerService
  ) {
    this.initializeVisualizationSubscriptions();
  }

  /**
   * Initialize NgRx subscriptions for visualization state
   */
  private initializeVisualizationSubscriptions(): void {
    // Subscribe to project models changes
    this.store.select(getProjectModelsWithFields)
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe(models => {
        if (models) {
          this.updateVisualizationState({ availableModels: models });
        }
      });

    // Handle model creation
    this.actions$.pipe(
      ofType(ProjectModelActions.ActionTypes.CreateProjectModelSuccessful),
      takeUntil(this.destroy$)
    ).subscribe((action: ProjectModelActions.CreateProjectModelSuccessful) => {
      this.handleModelCreated(action.payload.projectModel);
    });

    // Handle AI models creation
    this.actions$.pipe(
      ofType(ProjectModelActions.ActionTypes.SaveGeneratedDataModelsWithAISuccessful),
      takeUntil(this.destroy$)
    ).subscribe((action: ProjectModelActions.SaveGeneratedDataModelsWithAISuccessful) => {
      this.handleAIModelsCreated(action.payload.savedDataModels);
    });

    // Handle AI generation start
    this.actions$.pipe(
      ofType(ProjectModelActions.ActionTypes.SaveGeneratedDataModelsWithAI),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateVisualizationState({
        isLoading: true,
        loadingMessage: 'Creating data models...',
        shouldOpenSplitter: true
      });
    });

    // Handle successful model updates
    this.actions$.pipe(
      ofType(ProjectModelActions.ActionTypes.GetProjectModelsSuccessful),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const currentState = this.visualizationState$.value;
      if (currentState.selectedModel) {
        this.updateVisualizationState({ selectedModel: currentState.selectedModel });
      }
    });

    // Handle AI generation completion
    this.actions$.pipe(
      ofType(
        ProjectModelActions.ActionTypes.SaveGeneratedDataModelsWithAISuccessful,
        ProjectModelActions.ActionTypes.SaveGeneratedDataModelsWithAIFailure
      ),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateVisualizationState({
        isLoading: false,
        loadingMessage: ''
      });
    });

    // Handle entity component saved
    this.actions$.pipe(
      ofType(ProjectModelActions.ActionTypes.GetProjectModelsSuccessful),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.handleEntityComponentSaved();
    });
  }

  /**
   * Handle model creation and update visualization state
   * @param model - The created model to handle
   */
  private handleModelCreated(model: ProjectModelWithRelations): void {
    this.updateVisualizationState({
      lastCreatedModel: model,
      selectedModel: model,
      shouldOpenSplitter: true
    });
  }

  /**
   * Handle AI models creation and update visualization state
   * @param models - Array of created AI models
   */
  private handleAIModelsCreated(models: ProjectModelWithRelations[]): void {
    if (models && models.length > 0) {
      this.store.select(getProjectModelsWithFields)
        .pipe(takeUntil(this.destroy$))
        .subscribe(availableModels => {
          if (availableModels && availableModels.length > 0) {
            const latestModel = [...availableModels]
              .sort((a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime())[0];

            this.updateVisualizationState({
              lastCreatedModel: latestModel,
              selectedModel: latestModel,
              shouldOpenSplitter: true
            });
          }
        });
    }
  }

  /**
   * Handle entity component saved and update visualization state
   */
  private handleEntityComponentSaved(): void {
    this.store.select(getProjectModelsWithFields)
      .pipe(takeUntil(this.destroy$))
      .subscribe(availableModels => {
        if (availableModels && availableModels.length > 0) {
          const modelWithComponent = availableModels.find(model =>
            model.metadata?.some(meta => meta.key === 'displayWithComponent')
          );

          if (modelWithComponent) {
            this.updateVisualizationState({
              selectedModel: modelWithComponent,
              shouldOpenSplitter: true
            });
          }
        }
      });
  }

  /**
   * Update visualization state with partial state data
   * @param partialState - Partial state data to merge with current state
   */
  private updateVisualizationState(partialState: Partial<EntityVisualizationState>): void {
    const currentState = this.visualizationState$.value;
    this.visualizationState$.next({ ...currentState, ...partialState });
  }

  /**
   * Get visualization state as observable
   * @returns Observable stream of visualization state
   */
  getVisualizationState$(): Observable<EntityVisualizationState> {
    return this.visualizationState$.asObservable();
  }

  /**
   * Get current visualization state synchronously
   * @returns Current visualization state snapshot
   */
  getCurrentVisualizationState(): EntityVisualizationState {
    return this.visualizationState$.value;
  }

  /**
   * Select a model for visualization by ID
   * @param modelId - Unique identifier of the model to select
   */
  selectModel(modelId: string): void {
    const currentState = this.visualizationState$.value;
    const selectedModel = currentState.availableModels.find(model => model.id === modelId);

    if (selectedModel) {
      this.updateVisualizationState({
        selectedModel,
        shouldOpenSplitter: true
      });
    }
  }


  /**
   * Clear the splitter open flag
   */
  clearSplitterFlag(): void {
    this.updateVisualizationState({ shouldOpenSplitter: false });
  }

  /**
   * Get loading state as observable
   * @returns Observable stream of loading state and message
   */
  getLoadingState$(): Observable<{ isLoading: boolean; loadingMessage: string }> {
    return this.visualizationState$.pipe(
      map(state => ({
        isLoading: state.isLoading,
        loadingMessage: state.loadingMessage
      }))
    );
  }

  /**
   * Get selected model as observable
   * @returns Observable stream of selected model or null
   */
  getSelectedModel$(): Observable<ProjectModelWithRelations | null> {
    return this.visualizationState$.pipe(
      map(state => state.selectedModel),
      distinctUntilChanged()
    );
  }

  /**
   * Get splitter open flag as observable
   * @returns Observable stream of splitter open flag
   */
  getShouldOpenSplitter$(): Observable<boolean> {
    return this.visualizationState$.pipe(
      map(state => state.shouldOpenSplitter),
      distinctUntilChanged()
    );
  }

  /**
   * Get entity component bundle URL for rendering
   * Returns the main bundle URL with active version
   * @param projectModel - Project model to get component from
   * @returns Bundle URL string or null if not available
   */
  getEntityComponentBundleUrl(projectModel: ProjectModelWithRelations | null): string | null {
    const componentDefinitionId = this.getComponentDefinitionId(projectModel);
    if (!componentDefinitionId) {
      return null;
    }

    return this.componentGeneratorService.getMainBundleUrl(componentDefinitionId);
  }

  /**
   * Get component definition ID from project model metadata
   * Checks all framework keys in displayWithComponent (flutter, angular, undefined, etc.)
   * Prioritizes 'flutter' framework, then checks other keys
   * @param projectModel - Project model to get component ID from
   * @returns Component definition ID string or null if not found
   */
  getComponentDefinitionId(projectModel: ProjectModelWithRelations | null): string | null {
    if (!projectModel?.metadata) {
      return null;
    }

    try {
      const uiEntityConfig = projectModel.metadata.find(metadataItem => metadataItem.key === 'uiEntityConfig');
      if (!uiEntityConfig?.value) {
        return null;
      }

      const config = uiEntityConfig.value as Record<string, unknown>;
      const nestedConfig = config.config as Record<string, unknown> | undefined;
      if (!nestedConfig) {
        return null;
      }
      const displayConfig = nestedConfig.displayWithComponent as Record<string, unknown> | undefined;

      if (!displayConfig) {
        return null;
      }

      const frameworkKeys = ['flutter', ...Object.keys(displayConfig).filter(key => key !== 'flutter')];

      for (const frameworkKey of frameworkKeys) {
        const frameworkConfig = displayConfig[frameworkKey] as Record<string, unknown>;
        if (!frameworkConfig || typeof frameworkConfig !== 'object') {
          continue;
        }

        const itemConfig = frameworkConfig.item as Record<string, unknown>;
        const componentDefinitionId = itemConfig?.componentDefinitionId as string;

        if (componentDefinitionId) {
          return componentDefinitionId;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if project model has a display component configured
   * @param projectModel - Project model to check
   * @returns True if display component exists
   */
  hasDisplayComponent(projectModel: ProjectModelWithRelations | null): boolean {
    return !!(this.getComponentDefinitionId(projectModel));
  }

  ngOnDestroy(): void {
    this.visualizationState$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
